import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Save, Briefcase, Pencil } from 'lucide-react'
import { getCareers, addCareer, updateCareer, deleteCareer } from '../../lib/publicData'

const DEPT_BRANCH_MAP = {
  kitchen: 'Desres Kitchen',
  bakery: 'Desres Bakery',
  tuckshop: 'Desres Kitchen',
  yawehs: "Yahweh's Delight",
}

const BRANCH_OPTIONS = [
  { value: 'Desres Kitchen', color: 'terra' },
  { value: "Yahweh's Delight", color: 'honey' },
]

const TYPE_OPTIONS = ['Full-time', 'Part-time', 'Full-time / Part-time']

const EMPTY_JOB = {
  title: '',
  branch: 'Desres Kitchen',
  location: 'Limpopo, South Africa',
  type: 'Full-time',
  description: '',
  requirements: [''],
}

export default function CareersTab({ addToast, department }) {
  const deptBranch = DEPT_BRANCH_MAP[department]
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  const fetchJobs = async () => {
    setLoading(true)
    const data = await getCareers()
    setJobs(data)
    setLoading(false)
  }

  useEffect(() => { fetchJobs() }, [])

  const handleSave = async () => {
    if (!editing.title.trim() || !editing.description.trim()) {
      addToast('Title and description are required', 'error')
      return
    }
    setSaving(true)
    try {
      const data = {
        ...editing,
        requirements: editing.requirements.filter(r => r.trim()),
        location: editing.branch === 'Desres Kitchen' ? 'Limpopo, South Africa' : 'Pretoria, South Africa',
        color: editing.branch === 'Desres Kitchen' ? 'terra' : 'honey',
      }
      if (editing.id) {
        const { id, ...rest } = data
        await updateCareer(id, rest)
        addToast('Job updated', 'success')
      } else {
        await addCareer(data)
        addToast('Job created', 'success')
      }
      setEditing(null)
      fetchJobs()
    } catch {
      addToast('Failed to save job', 'error')
    }
    setSaving(false)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this job listing?')) return
    try {
      await deleteCareer(id)
      addToast('Job deleted', 'success')
      fetchJobs()
    } catch {
      addToast('Failed to delete', 'error')
    }
  }

  if (loading) return <p className="text-gray-500 text-sm text-center py-8">Loading careers...</p>

  if (editing) {
    return (
      <div className="space-y-4 max-w-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{editing.id ? 'Edit Job' : 'New Job Listing'}</h3>
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Job Title</label>
            <input
              type="text"
              value={editing.title}
              onChange={e => setEditing(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Head Chef"
              className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Branch</label>
            <select
              value={editing.branch}
              onChange={e => setEditing(p => ({ ...p, branch: e.target.value }))}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
            >
              {BRANCH_OPTIONS.map(b => <option key={b.value} value={b.value}>{b.value}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Employment Type</label>
            <select
              value={editing.type}
              onChange={e => setEditing(p => ({ ...p, type: e.target.value }))}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
            >
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Description</label>
          <textarea
            value={editing.description}
            onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
            rows={4}
            placeholder="Describe the role..."
            className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
          />
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Requirements</label>
          <div className="space-y-2">
            {editing.requirements.map((req, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  value={req}
                  onChange={e => {
                    const reqs = [...editing.requirements]
                    reqs[i] = e.target.value
                    setEditing(p => ({ ...p, requirements: reqs }))
                  }}
                  placeholder={`Requirement ${i + 1}`}
                  className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white flex-1"
                />
                {editing.requirements.length > 1 && (
                  <button
                    onClick={() => setEditing(p => ({ ...p, requirements: p.requirements.filter((_, j) => j !== i) }))}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => setEditing(p => ({ ...p, requirements: [...p.requirements, ''] }))}
              className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1"
            >
              <Plus size={12} /> Add requirement
            </button>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-5 py-2 rounded-lg text-sm font-semibold"
          >
            <Save size={14} />
            {saving ? 'Saving...' : editing.id ? 'Update Job' : 'Create Job'}
          </button>
          <button onClick={() => setEditing(null)} className="bg-gray-700 text-gray-300 px-4 py-2 rounded-lg text-sm">
            Cancel
          </button>
        </div>
      </div>
    )
  }

  const deptJobs = deptBranch ? jobs.filter(j => j.branch === deptBranch) : jobs

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{deptJobs.length} job listing{deptJobs.length !== 1 ? 's' : ''} · {deptBranch}</p>
        <button
          onClick={() => setEditing({ ...EMPTY_JOB, branch: deptBranch || EMPTY_JOB.branch, location: deptBranch === "Yahweh's Delight" ? 'Pretoria, South Africa' : 'Limpopo, South Africa' })}
          className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={14} /> Add Job
        </button>
      </div>

      {deptJobs.length === 0 ? (
        <div className="text-center py-12">
          <Briefcase size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No job listings yet</p>
          <p className="text-gray-600 text-xs mt-1">Add jobs here and they'll appear on the public site</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deptJobs.map(job => (
            <div key={job.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-white">{job.title}</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                      job.color === 'terra' ? 'bg-orange-900 text-orange-300' : 'bg-amber-900 text-amber-300'
                    }`}>
                      {job.branch}
                    </span>
                    <span className="text-[10px] text-gray-500">{job.type}</span>
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-2">{job.description}</p>
                  {job.requirements?.length > 0 && (
                    <p className="text-[10px] text-gray-500 mt-1">{job.requirements.length} requirement{job.requirements.length !== 1 ? 's' : ''}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditing({ ...job })}
                    className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(job.id)}
                    className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
